import './SearchableSelect.scss';

import cn from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SelectPicker } from 'rsuite';

import { ReactComponent as ArrowIcon } from '@/common/assets/icons/arrow-list.svg';

import HtmlElement from '../../atoms/HtmlElement/HtmlElement';
import FieldErrors from '../FieldErrors/FieldErrors';
import {
  SearchableSelectDefaultProps,
  SearchableSelectPropTypes,
} from './SearchableSelect.proptypes';

const SearchableSelect = ({
  className,
  children,
  context,
  data,
  disabled,
  errors,
  errorsIcon,
  id,
  name,
  placeholder,
  position,
  readOnly,
  required,
  style,
  onChange,
  defaultValue,
}) => {
  const [selectedItem, setSelectedItem] = useState(defaultValue || null);

  const selectRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [maxMenuHeight, setMaxMenuHeight] = useState(null);

  const conditionalProps = useMemo(
    () => ({
      ...(id ? { id } : {}),
      ...(context
        ? { 'data-qa-id': `${context}-${disabled ? 'disabled' : 'active'}` }
        : {}),
    }),
    [context, disabled, id],
  );

  const handleSelect = (value) => {
    setSelectedItem({ value });
    setIsOpen(!isOpen);
    if (onChange) {
      onChange(value);
    }
  };

  const handleClickOutside = useCallback((e) => {
    if (selectRef?.current && selectRef?.current.contains(e.target)) {
      return;
    }
    setIsOpen(false);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  useEffect(() => {
    const calculateMaxMenuHeight = () => {
      const selectField = selectRef.current;
      if (selectField) {
        const windowHeight = window.innerHeight;
        const rect = selectField.getBoundingClientRect();
        const distanceToBottom = windowHeight - rect.bottom;

        const buffer = 20;

        // Calcule la hauteur maximale en tenant compte du buffer
        const maxHeight = distanceToBottom - buffer;
        setMaxMenuHeight(maxHeight);
      }
    };

    calculateMaxMenuHeight();

    window.addEventListener('resize', calculateMaxMenuHeight);

    return () => {
      window.removeEventListener('resize', calculateMaxMenuHeight);
    };
  }, [position]);

  return (
    <div
      className={cn(
        'select-field',
        'searchable-select',
        {
          'select-field--disabled': disabled,
          'select-field--readonly': readOnly,
          'select-field--error': errors && errors.filter((val) => val).length,
          [`select-field--${position}`]: position,
          'select-field--required': required,
        },
        className,
      )}
      id={id}
      data-qa-id="searchable-select"
    >
      <HtmlElement
        className="select-field__label"
        htmlFor={`${id}-select`}
        id={`${id}-label`}
        tag="label"
      >
        {children}
      </HtmlElement>
      <div
        className="select-field__select"
        ref={selectRef}
        data-qa-id="select-field-select"
      >
        <SelectPicker
          className={cn('searchable-select', {
            'searchable-select-isOpen': isOpen,
          })}
          container={() => selectRef.current}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...conditionalProps}
          data={data}
          style={style}
          menuStyle={{ maxHeight: maxMenuHeight }}
          onClick={handleToggle}
          searchable
          cleanable={false}
          locale={{ searchPlaceholder: '' }}
          value={selectedItem?.value ?? null}
          caretAs={ArrowIcon}
          onChange={handleSelect}
          placeholder={placeholder}
        />
      </div>
      {errors && (
        <FieldErrors
          className="select-field__errors"
          errors={errors}
          icon={errorsIcon}
          name={name}
        />
      )}
    </div>
  );
};

SearchableSelect.defaultProps = {
  ...SearchableSelectDefaultProps,
};

SearchableSelect.propTypes = {
  ...SearchableSelectPropTypes,
};

export default SearchableSelect;
